import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import clsx from "clsx";
 import Swal from 'sweetalert2';
 import { Clock, Trophy, ShieldCheck } from "lucide-react";

export default function Dashboard() {
  // --- ESTADOS DE DATOS ---
  const [activeTab, setActiveTab] = useState('ranking');
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [ranking, setRanking] = useState([]);
  const [teams, setTeams] = useState([]); 
  const [news, setNews] = useState([]); 
  const [user, setUser] = useState(null);

  // --- ESTADOS DE UI Y CONTROL ---
  const [isSaving, setIsSaving] = useState(false);
  const [selectedMatchPredictions, setSelectedMatchPredictions] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [isAdminPodiumModalOpen, setIsAdminPodiumModalOpen] = useState(false);
  const [toastConfig, setToastConfig] = useState({ show: false, message: '', isError: false });

  // --- ESTADOS DE PODIO (USUARIO Y REAL) ---
  const [podio, setPodio] = useState({ 
    champion: '', 
    subchampion: '', 
    third: '', 
    fourth: '' 
  });
  const [realPodium, setRealPodium] = useState({ 
    champion: '', 
    subchampion: '', 
    third: '', 
    fourth: '' 
  });
  const [yaVotoPodio, setYaVotoPodio] = useState(false);
  const PUNTOS_PODIO = {
  champion: 40,
  subchampion: 20,
  third: 10,
  fourth: 10
};

  const usersPerPage = 20;
  const navigate = useNavigate();

  // --- FUNCIÓN DE CARGA PRINCIPAL ---
  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return navigate('/login');
    }

    const config = { 
      headers: { Authorization: `Bearer ${token}` } 
    };

    try {
   const [resM, resP, resR, resU, resT, resPR] = await Promise.allSettled([
  api.get('/matches', config),
  api.get('/predictions/me', config),
  api.get('/rankings', config),
  api.get('/users/me', config),
  api.get('/teams', config),
  api.get('/tournament-results?tenantId=localhost', config) // <--- ESTA ES LA 6ta PETICIÓN
]);

      // Procesar Equipos
      if (resT.status === 'fulfilled') {
        const sortedTeams = resT.value.data.sort((a, b) => a.name.localeCompare(b.name));
        setTeams(sortedTeams);
      }

      // Procesar Partidos
      if (resM.status === 'fulfilled') {
        setMatches(resM.value.data);
      }

      if (resPR.status === 'fulfilled' && resPR.value.data) {
  setRealPodium(resPR.value.data); // Esto llena el estado realPodium con lo que hay en la DB
}
      
      // Procesar Usuario y su Podio
      if (resU.status === 'fulfilled') {
        const userData = resU.value.data;
        setUser(userData);
        if (userData.pred_champion) {
          setPodio({
            champion: userData.pred_champion,
            subchampion: userData.pred_subchampion,
            third: userData.pred_third,
            fourth: userData.pred_fourth
          });
          setYaVotoPodio(true);
        }
      }

      // Procesar Predicciones Guardadas
      if (resP.status === 'fulfilled') {
        const saved = {};
        resP.value.data.forEach(p => {
          saved[p.match.id] = { 
            home: p.predictedHomeScore, 
            away: p.predictedAwayScore, 
            points: p.points 
          };
        });
        setPredictions(saved);
      }

      // Procesar Ranking
      if (resR.status === 'fulfilled') {
        setRanking(resR.value.data);
      }

    } catch (err) { 
      console.error('Error crítico cargando dashboard', err); 
    }
  };

  // --- CARGA DE NOTICIAS EXTERNAS ---
  const loadNews = async () => {
    try {
      const rssUrl = "https://www.espn.com.mx/espn/rss/futbol/mundial";
      const res = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`);
      const data = await res.json();
      
      if (data.status === 'ok') {
        const processedNews = data.items.map((item, index) => {
          let img = item.enclosure?.link || item.thumbnail;
          if (!img && item.description) {
            const match = item.description.match(/src="([^">]+)"/);
            if (match) img = match[1];
          }
          return { 
            ...item, 
            image: img || `https://images.unsplash.com/photo-1574629810360-7efbbe195018?soccer=${index}` 
          };
        });
        setNews(processedNews.slice(0, 6));
      }
    } catch (err) { 
      console.error("Error cargando noticias RSS", err); 
    }
  };

  useEffect(() => { 
    loadData(); 
    loadNews(); 
  }, [navigate]);

  // --- GESTIÓN DE PREDICCIONES ---
  const saveAll = async () => {
    setIsSaving(true);
    const ahora = new Date();
    const diezMinutos = 10 * 60 * 1000;
    
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      
      const reqs = Object.entries(predictions)
        .map(([id, val]) => {
          const partido = matches.find(m => m.id === parseInt(id));
          
          if (!partido) return null;
          
          // Validación de tiempo (10 min antes)
          const tiempoLimite = new Date(partido.matchDate).getTime() - diezMinutos;
          if (ahora.getTime() > tiempoLimite) return null;
          
          if (val.home === '' || val.away === '' || val.home === null || val.away === null) return null;
          
          return api.post('/predictions', {
            match: { id: parseInt(id) },
            predictedHomeScore: parseInt(val.home),
            predictedAwayScore: parseInt(val.away)
          }, config);
        }).filter(Boolean);

      if (reqs.length === 0) {
        setToastConfig({ show: true, message: 'Nada nuevo para guardar o tiempo agotado', isError: true });
      } else {
        await Promise.all(reqs);
        setToastConfig({ show: true, message: '¡Pronósticos guardados correctamente!', isError: false });
        loadData();
      }
    } catch (err) { 
      setToastConfig({ show: true, message: 'Error al conectar con el servidor', isError: true }); 
    } finally { 
      setIsSaving(false); 
      setTimeout(() => setToastConfig(prev => ({ ...prev, show: false })), 3000); 
    }
  };

 

const savePodio = async () => {
  if (!podio.champion || !podio.subchampion || !podio.third || !podio.fourth) {
    setToastConfig({ show: true, message: 'Debes completar los 4 puestos', isError: true });
    setTimeout(() => setToastConfig(prev => ({ ...prev, show: false })), 3000);
    return;
  }

  const result = await Swal.fire({
    title: '¿Confirmar podio?',
    html: `
      <p>⚠️ <b>Esta acción es definitiva</b></p>
      <p>No podrás modificar el podio durante todo el torneo.</p>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, guardar podio',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#d33'
  });

  if (!result.isConfirmed) return;

  setIsSaving(true);
  try {
    const config = {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    };
    await api.post('/users/update-podio', podio, config);
    setYaVotoPodio(true);
    setToastConfig({ show: true, message: '🏆 Podio guardado permanentemente', isError: false });
    loadData();
  } catch (err) {
    setToastConfig({ show: true, message: 'No se pudo guardar el podio', isError: true });
  } finally {
    setIsSaving(false);
    setTimeout(() => setToastConfig(prev => ({ ...prev, show: false })), 3000);
  }
};

 
  // --- LÓGICA DE ADMINISTRADOR ---
// Dentro de Dashboard.jsx
const handleAdminCalculatePodium = async () => {
  if (!realPodium.champion || !realPodium.subchampion) {
      setToastConfig({ show: true, message: 'Datos incompletos', isError: true });
      return;
  }
  setIsSaving(true);
  try {
    const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    
    // CAMBIO: Usar el nuevo endpoint igual que en AdminPodium.jsx
    await api.post('/tournament-results?tenantId=localhost', realPodium, config);
    
    setToastConfig({ show: true, message: '✅ ¡Puntos repartidos con éxito!', isError: false });
    setIsAdminPodiumModalOpen(false);
    loadData(); // Recarga el ranking para ver los nuevos puntos
  } catch (err) {
    setToastConfig({ show: true, message: 'Error en el cálculo', isError: true });
  } finally {
    setIsSaving(false);
    setTimeout(() => setToastConfig(prev => ({ ...prev, show: false })), 3000);
  }
};

  // --- ESTADÍSTICAS Y MODAL DE RIVALES ---
  const obtenerEstadisticas = (listaPreds) => {
    const validas = listaPreds.filter(p => p.predictedHomeScore !== null && !p.virtual);
    if (validas.length === 0) return [];
    
    const conteo = {};
    validas.forEach(p => {
      const marcador = `${p.predictedHomeScore}-${p.predictedAwayScore}`;
      conteo[marcador] = (conteo[marcador] || 0) + 1;
    });

    return Object.entries(conteo)
      .map(([marcador, cantidad]) => ({ 
        marcador, 
        porcentaje: Math.round((cantidad / validas.length) * 100) 
      }))
      .sort((a, b) => b.porcentaje - a.porcentaje)
      .slice(0, 3);
  };

  const verPrediccionesPartido = async (matchId) => {
    try {
      const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
      const res = await api.get(`/predictions/match/${matchId}`, config);
      const matchObj = matches.find(m => m.id === matchId);
      
      setSelectedMatchPredictions({
        match: matchObj,
        predictions: res.data,
        stats: obtenerEstadisticas(res.data)
      });
    } catch (err) { 
        console.error("Error al obtener predicciones de la comunidad", err); 
    }
  };

  const futuros = matches.filter(m => m.scoreHome === null); 
  const pasados = matches.filter(m => m.scoreHome !== null);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans text-slate-900 mt-16">
      <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-3xl overflow-hidden border border-slate-200">

        {/* --- CABECERA --- */}
        <div className="bg-slate-900 p-6 text-center text-white relative">
          {user?.role === 'admin' && (
            <div className="absolute top-4 right-4 flex flex-col items-end">
              <button 
                onClick={() => setShowAdminMenu(!showAdminMenu)} 
                className="bg-red-600 hover:bg-red-700 text-white text-[9px] font-black px-4 py-2 rounded-lg uppercase tracking-widest transition-all shadow-xl z-50 border border-red-500"
              >
                ⚙️ {showAdminMenu ? 'Cerrar' : 'Admin'}
              </button>
              {showAdminMenu && (
                <div className="bg-white shadow-2xl rounded-xl mt-2 overflow-hidden border border-slate-200 flex flex-col z-50 w-40 animate-in slide-in-from-top-2 duration-200">
                  <button onClick={() => navigate('/admin-matches')} className="px-4 py-3 text-[9px] font-black uppercase text-slate-700 hover:bg-slate-50 border-b border-slate-100 text-right">⚽ Partidos</button>
                  <button onClick={() => { setIsAdminPodiumModalOpen(true); setShowAdminMenu(false); }} className="px-4 py-3 text-[9px] font-black uppercase text-slate-700 hover:bg-slate-50 text-right">🏆 Podio Final</button>
                </div>
              )}
            </div>
          )}
          <h1 className="text-2xl font-black tracking-tight uppercase italic text-white">🏆 Polla Mundial 2026</h1>
          {user && (
            <p className="text-blue-400 text-[10px] font-bold mt-1 uppercase tracking-widest">
              Hola, {user.name} <span className="opacity-30 text-white">|</span> <span className="text-white">{user.role}</span>
            </p>
          )}
        </div>

        {/* --- NAVEGACIÓN --- */}
        <div className="flex justify-center bg-slate-100 p-2 border-b gap-1 overflow-x-auto">
          {[
            { id: 'ranking', label: '📊 Ranking' }, 
            { id: 'historial', label: '⌛ Resultados' }, 
            { id: 'proximos', label: '⚽ Próximos' }, 
            { id: 'podio', label: '🏆 Podio' }, 
            { id: 'noticias', label: '📰 Noticias' }
          ].map(tab => (
            <button 
                key={tab.id} 
                onClick={() => { setActiveTab(tab.id); setCurrentPage(1); }} 
                className={`px-4 md:px-8 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-white text-blue-600 shadow-md scale-105' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-4 md:p-8">
          
          {/* SECCIÓN: RANKING */}
          {activeTab === 'ranking' && (
            <div className="animate-in fade-in duration-500 px-2 md:px-6">
              <div className="max-w-2xl mx-auto">
                <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 mb-1">
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Posición / Jugador</span>
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Puntos Totales</span>
                </div>
                <div className="space-y-1">
                  {ranking.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage).map((row, index) => {
                    const actualIndex = ((currentPage - 1) * usersPerPage) + index;
                    const isMe = row.name === user?.name;
                    const initials = row.name ? row.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '??';
                    return (
                      <div key={actualIndex} className={clsx("group flex items-center justify-between p-2 rounded-xl transition-all border", isMe ? "bg-blue-50/50 border-blue-200 shadow-sm" : "bg-white border-transparent hover:border-slate-200 hover:bg-slate-50/50")}>
                        <div className="flex items-center gap-3">
                          <span className={clsx("w-6 h-6 flex items-center justify-center rounded-lg text-[10px] font-black shrink-0", actualIndex === 0 ? 'text-yellow-600 bg-yellow-50' : actualIndex === 1 ? 'text-slate-500 bg-slate-50' : 'text-slate-400')}>{actualIndex + 1}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-slate-900 flex items-center justify-center text-[9px] font-bold text-white shadow-sm">{initials}</div>
                            <span className={clsx("text-[11px] font-bold", isMe ? "text-blue-900" : "text-slate-700")}>{row.name}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="bg-white border border-slate-200 px-2.5 py-0.5 rounded-lg shadow-sm"><span className="text-[11px] font-black text-slate-900">{row.totalpoints || 0}</span></div>
                          <button onClick={() => navigate(`/user-predictions/${row.userId || row.id}`)} className="p-1.5 text-slate-400 hover:text-slate-900 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Paginación con lógica completa */}
                {ranking.length > usersPerPage && (
                  <div className="flex justify-center gap-2 mt-8">
                    {Array.from({ length: Math.ceil(ranking.length / usersPerPage) }).map((_, i) => (
                      <button key={i} onClick={() => { setCurrentPage(i+1); window.scrollTo(0,0); }} className={clsx("w-8 h-8 rounded-lg text-[10px] font-black transition-all", currentPage === i+1 ? "bg-slate-900 text-white shadow-lg" : "bg-slate-100 text-slate-400 hover:bg-slate-200")}>{i+1}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SECCIÓN: PRÓXIMOS PARTIDOS */}
          {activeTab === 'proximos' && (
            <div className="space-y-3 animate-in fade-in duration-500 pb-32">
              {futuros.length === 0 ? (
                <div className="text-center py-20 text-slate-300 font-black uppercase text-[10px] tracking-widest">No hay partidos próximos disponibles</div>
              ) : (
                futuros.map(m => {
                  const isBlocked = (new Date(m.matchDate) - new Date()) < (10 * 60 * 1000);
                  return (
                    <div key={m.id} className="bg-white rounded-[28px] border border-slate-100 p-3 px-5 shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                      <div className="w-20">
                        <span className="block text-blue-600 font-black text-[10px] uppercase tracking-tighter">{new Date(m.matchDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}</span>
                        <span className="text-slate-400 text-[9px] font-bold uppercase flex items-center gap-1 mt-1">{isBlocked ? '🔒' : '🕒'} {new Date(m.matchDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                      </div>
                      <div className="flex-1 flex items-center justify-center gap-3">
                        <div className="flex items-center gap-2 flex-1 justify-end">
                          <span className="text-[10px] font-black text-slate-600 uppercase hidden md:inline">{m.homeTeam?.name}</span>
                          <img src={m.homeTeam?.flagUrl} className="w-6 h-4 object-cover rounded shadow-sm" alt="" />
                          <input 
                            type="number" 
                            disabled={isBlocked} 
                            value={predictions[m.id]?.home ?? ''} 
                            onChange={e => setPredictions({ ...predictions, [m.id]: { ...predictions[m.id], home: e.target.value } })} 
                            className="w-10 h-10 rounded-2xl text-center font-black text-sm outline-none transition-all bg-blue-50/40 border border-blue-100 focus:bg-white focus:ring-4 focus:ring-blue-500/10" 
                          />
                        </div>
                        <span className="text-slate-200 font-black italic text-[10px]">VS</span>
                        <div className="flex items-center gap-2 flex-1 justify-start">
                          <input 
                            type="number" 
                            disabled={isBlocked} 
                            value={predictions[m.id]?.away ?? ''} 
                            onChange={e => setPredictions({ ...predictions, [m.id]: { ...predictions[m.id], away: e.target.value } })} 
                            className="w-10 h-10 rounded-2xl text-center font-black text-sm outline-none transition-all bg-blue-50/40 border border-blue-100 focus:bg-white focus:ring-4 focus:ring-blue-500/10" 
                          />
                          <img src={m.awayTeam?.flagUrl} className="w-6 h-4 object-cover rounded shadow-sm" alt="" />
                          <span className="text-[10px] font-black text-slate-600 uppercase hidden md:inline">{m.awayTeam?.name}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[50] w-full max-w-xs px-4">
                <button 
                  onClick={saveAll} 
                  disabled={isSaving} 
                  className="w-full bg-slate-900 hover:bg-blue-600 text-white py-4 rounded-[24px] shadow-2xl flex items-center justify-center gap-3 transition-all transform hover:scale-105 active:scale-95 border-b-4 border-black/20"
                >
                  <span className="text-[11px] font-black uppercase tracking-[0.2em]">{isSaving ? 'Guardando...' : 'Guardar Cambios'}</span>
                </button>
              </div>
            </div>
          )}

          {/* SECCIÓN: RESULTADOS PASADOS */}
          {activeTab === 'historial' && (
            <div className="space-y-2 animate-in fade-in duration-500">
              {pasados.map(m => {
                const userPred = predictions[m.id];
                const points = userPred?.points ?? 0;
                return (
                  <div key={m.id} onClick={() => verPrediccionesPartido(m.id)} className="group bg-white rounded-xl border border-slate-100 p-2 px-4 shadow-sm flex items-center justify-between cursor-pointer hover:border-blue-200 transition-all">
                    <div className="flex items-center gap-3 w-16">
                      <div className="flex flex-col items-center justify-center bg-slate-900 rounded-lg py-1 w-full shadow-sm group-hover:bg-blue-600 transition-colors">
                        <span className="text-[11px] font-black text-white leading-none">{m.scoreHome}-{m.scoreAway}</span>
                        <span className="text-[7px] font-bold text-red-500 group-hover:text-white uppercase tracking-tighter mt-0.5">Final</span>
                      </div>
                    </div>
                    <div className="flex-1 px-4 text-center">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <img src={m.homeTeam?.flagUrl} className="w-4 h-2.5 object-cover rounded-sm" alt="" />
                        <span className="text-[10px] font-black text-slate-700 uppercase">{m.homeTeam?.name?.substring(0,3)} vs {m.awayTeam?.name?.substring(0,3)}</span>
                        <img src={m.awayTeam?.flagUrl} className="w-4 h-2.5 object-cover rounded-sm" alt="" />
                      </div>
                    </div>
                    <div className="w-16 flex flex-col items-end">
                      <span className="text-[8px] font-bold text-slate-400 uppercase leading-none">Tu Pred</span>
                      <span className="text-[10px] font-black text-slate-800">{userPred ? `${userPred.home}-${userPred.away}` : '--'}</span>
                      <span className={clsx("text-[9px] font-black mt-0.5", points > 0 ? "text-green-600" : "text-slate-300")}>+{points} pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

{/* SECCIÓN: PODIO FINAL */}
{activeTab === 'podio' && (
  <div className="max-w-md mx-auto animate-in fade-in duration-500 py-4">
    
    {/* BANNER INFORMATIVO DE TIEMPO LÍMITE */}
    {!yaVotoPodio && (
      <div className="mb-6 bg-gradient-to-r from-amber-500 to-orange-600 p-[1px] rounded-[30px] shadow-lg shadow-orange-200/40">
        <div className="bg-white rounded-[29px] p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center shrink-0 animate-pulse">
            <Clock className="text-amber-600" size={24} />
          </div>
          <div>
            <h4 className="text-[11px] font-black uppercase tracking-tighter text-amber-600 leading-none mb-1">
              ¡Atención Goleador!
            </h4>
            <p className="text-[10px] text-slate-500 font-bold leading-tight uppercase">
              El podio se bloquea <span className="text-slate-900 underline">10 minutos antes</span> del partido inaugural. ¡Define tu top 4 ahora!
            </p>
          </div>
        </div>
      </div>
    )}

    <div className="text-center mb-8">
      <h2 className="text-2xl font-black uppercase italic text-slate-900">Cuadro de Honor</h2>
      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
        {yaVotoPodio ? "Tu apuesta ha sido bloqueada" : "Tu apuesta vs Resultado Real"}
      </p>
    </div>

    <div className="space-y-4 bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner relative overflow-hidden">
      {/* Icono de Trofeo decorativo de fondo */}
      <Trophy className="absolute -right-6 -bottom-6 text-slate-200/40 rotate-12" size={140} />
      
      {/* Resumen de puntos ganados (Solo visible tras el mundial) */}
      {yaVotoPodio && realPodium.champion && (
        <div className="mb-6 bg-white p-4 rounded-2xl border-2 border-dashed border-blue-200 text-center animate-in zoom-in duration-300 relative z-10">
          <span className="block text-[9px] font-black text-slate-400 uppercase">Puntos obtenidos en Podio</span>
          <span className="text-2xl font-black text-blue-600">
            +{Object.keys(PUNTOS_PODIO).reduce((acc, key) => 
               podio[key] === realPodium[key] ? acc + PUNTOS_PODIO[key] : acc, 0)} pts
          </span>
        </div>
      )}

      {/* Renderizado de los 4 puestos */}
      {[
        { id: 'champion', label: '🥇 Campeón', color: 'text-yellow-600' }, 
        { id: 'subchampion', label: '🥈 Subcampeón', color: 'text-slate-500' }, 
        { id: 'third', label: '🥉 Tercer Puesto', color: 'text-orange-600' }, 
        { id: 'fourth', label: '🏅 Cuarto Puesto', color: 'text-blue-600' }
      ].map((pos) => {
        const selectedTeam = teams.find(t => t.name === podio[pos.id]);
        const isCorrect = realPodium[pos.id] && podio[pos.id] === realPodium[pos.id];
        const hasResult = !!realPodium[pos.id];

        return (
          <div key={pos.id} className="relative group z-10">
            <div className="flex justify-between items-end ml-4 mb-1.5">
              <label className={clsx("text-[9px] font-black uppercase tracking-widest", pos.color)}>
                {pos.label}
              </label>
              {hasResult && (
                <span className={clsx(
                  "text-[10px] font-black uppercase px-2 py-0.5 rounded-md",
                  isCorrect ? "bg-green-100 text-green-600" : "bg-red-100 text-red-400"
                )}>
                  {isCorrect ? `+${PUNTOS_PODIO[pos.id]} PTS` : "0 PTS"}
                </span>
              )}
            </div>

            <div className="relative flex items-center">
              {selectedTeam && (
                <img 
                  src={selectedTeam.flagUrl} 
                  className="absolute left-4 w-6 h-4 object-cover rounded shadow-sm z-10 pointer-events-none" 
                  alt="" 
                />
              )}
              
              <select
                disabled={yaVotoPodio || isSaving}
                value={podio[pos.id] || ""}
                onChange={(e) => setPodio({ ...podio, [pos.id]: e.target.value })}
                className={clsx(
                  "w-full px-12 py-3.5 rounded-2xl text-[12px] font-black uppercase border appearance-none outline-none transition-all",
                  yaVotoPodio 
                    ? "bg-white/80 border-slate-200 text-slate-700 cursor-not-allowed" 
                    : "bg-white border-blue-200 text-blue-900 focus:ring-4 focus:ring-blue-500/10 cursor-pointer hover:border-blue-400"
                )}
              >
                <option value="" disabled>Seleccionar equipo...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.name}>
                    {team.name}
                  </option>
                ))}
              </select>

              {hasResult && (
                <span className="absolute right-4 text-lg">
                  {isCorrect ? '✅' : '❌'}
                </span>
              )}
            </div>
            
            {hasResult && !isCorrect && (
              <p className="ml-4 mt-1 text-[8px] font-bold text-slate-400 uppercase italic">
                Resultado real: <span className="text-slate-600">{realPodium[pos.id]}</span>
              </p>
            )}
          </div>
        );
      })}

      {/* BOTÓN O MENSAJE DE CIERRE */}
      {!yaVotoPodio && (
        <div className="mt-8 animate-in slide-in-from-bottom-2 duration-500 relative z-10">
          <button
            onClick={savePodio}
            disabled={isSaving}
            className={clsx(
              "w-full py-4 rounded-2xl font-black uppercase tracking-widest transition-all shadow-lg",
              isSaving 
                ? "bg-slate-100 text-slate-400 cursor-not-allowed" 
                : "bg-green-600 text-white hover:bg-green-700 active:scale-95 border-b-4 border-green-800 shadow-green-200"
            )}
          >
            {isSaving ? 'PROCESANDO...' : '🔒 REGISTRAR PODIO FINAL'}
          </button>
          
          <div className="flex items-center justify-center gap-2 mt-4 bg-slate-200/50 py-2 px-4 rounded-full w-fit mx-auto">
            <ShieldCheck size={12} className="text-slate-500" />
            <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest">
              Límite: 10 min antes del primer partido
            </p>
          </div>
        </div>
      )}
    </div>
  </div>
)}
          {/* SECCIÓN: NOTICIAS */}
          {activeTab === 'noticias' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-500">
              {news.map((item, idx) => (
                <a key={idx} href={item.link} target="_blank" rel="noopener noreferrer" className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group">
                  <div className="aspect-[16/9] w-full bg-slate-200 overflow-hidden">
                    <img src={item.image} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Mundial 2026</span>
                    <h3 className="text-[13px] font-black text-slate-800 mt-2 line-clamp-2 leading-tight uppercase italic">{item.title}</h3>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* --- MODAL ESTADÍSTICAS COMUNIDAD --- */}
      {selectedMatchPredictions && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-lg rounded-[40px] shadow-2xl overflow-hidden border border-slate-100">
            <div className="bg-slate-50 p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-black uppercase italic text-slate-900">Análisis de la Comunidad</h3>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Resultados más frecuentes</p>
              </div>
              <button onClick={() => setSelectedMatchPredictions(null)} className="w-8 h-8 rounded-full bg-white shadow-sm border border-slate-200 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-all text-slate-400 font-bold">✕</button>
            </div>
            <div className="p-6">
              {selectedMatchPredictions.stats.length > 0 && (
                <div className="mb-6 bg-blue-50/50 rounded-3xl p-6 border border-blue-100/50">
                  <div className="flex justify-around items-end gap-2 h-24">
                    {selectedMatchPredictions.stats.map((s, i) => (
                      <div key={i} className="flex flex-col items-center gap-2 flex-1">
                        <span className="text-[10px] font-black text-slate-900 bg-white px-2 py-1 rounded-lg shadow-sm border border-slate-100">{s.marcador}</span>
                        <div className="w-full bg-blue-600 rounded-t-xl transition-all duration-1000 shadow-[0_-5px_15px_rgba(37,99,235,0.2)]" style={{ height: `${s.porcentaje}%`, minHeight: '15%' }}></div>
                        <span className="text-[11px] font-black text-blue-600">{s.porcentaje}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="max-h-60 overflow-y-auto pr-2 space-y-2 custom-scrollbar">
                {selectedMatchPredictions.predictions.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-2xl bg-slate-50/50 border border-slate-100 transition-all hover:bg-white">
                    <span className="text-[11px] font-bold text-slate-700">{p.user?.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-black text-slate-900 bg-white px-3 py-1 rounded-lg border border-slate-200 shadow-sm">{p.predictedHomeScore} - {p.predictedAwayScore}</span>
                      <span className={clsx("text-[10px] font-black w-8 text-right", p.points > 0 ? "text-green-600" : "text-slate-300")}>+{p.points}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL ADMIN: REPARTO DE PUNTOS PODIO --- */}
      {isAdminPodiumModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-[32px] shadow-2xl overflow-hidden p-8 border border-slate-200 relative">
            <div className="text-center mb-6">
              <span className="text-red-600 font-black text-[10px] uppercase tracking-widest bg-red-50 px-3 py-1 rounded-full">Gestión Oficial</span>
              <h2 className="text-xl font-black uppercase italic text-slate-900 mt-2">Definir Podio Real</h2>
              <p className="text-[9px] text-slate-400 font-bold uppercase mt-1">Ingresa los ganadores finales para procesar los puntos de todos los usuarios</p>
            </div>
            <div className="space-y-4">
              {[
                { id: 'champion', label: '🏆 Campeón Mundial' }, 
                { id: 'subchampion', label: '🥈 Segundo Lugar' }, 
                { id: 'third', label: '🥉 Tercer Lugar' }, 
                { id: 'fourth', label: '🏅 Cuarto Lugar' }
              ].map((pos) => (
                <div key={pos.id}>
                  <label className="text-[8px] font-black uppercase text-slate-400 ml-2 mb-1 block">{pos.label}</label>
                  <select 
                    value={realPodium[pos.id]} 
                    onChange={(e) => setRealPodium({...realPodium, [pos.id]: e.target.value})} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[12px] font-bold outline-none focus:border-red-500 transition-all focus:bg-white"
                  >
                    <option value="">-- Seleccionar Equipo --</option>
                    {teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-8">
              <button onClick={() => setIsAdminPodiumModalOpen(false)} className="flex-1 bg-slate-100 text-slate-400 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-colors">Cancelar</button>
              <button 
                onClick={handleAdminCalculatePodium} 
                disabled={isSaving} 
                className="flex-[2] bg-red-600 text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50"
              >
                {isSaving ? 'Procesando...' : '🔥 Ejecutar Cálculo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- COMPONENTE DE NOTIFICACIONES TOAST --- */}
      {toastConfig.show && (
        <div className="fixed bottom-28 left-1/2 -translate-x-1/2 z-[150] animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className={clsx("px-6 py-3 rounded-2xl shadow-2xl border flex items-center gap-3 text-white transition-all", toastConfig.isError ? "bg-red-600 border-red-400" : "bg-slate-900 border-slate-700")}>
            <span className="text-[10px] font-black uppercase tracking-wider">{toastConfig.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}